import React, { useState, useEffect } from 'react';
import { getStatistics } from '../../api/client';

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format hour as readable time (e.g., 14 -> "2PM")
 */
function formatHour(hour) {
  if (hour == null || isNaN(hour)) return 'N/A';
  if (hour === 0) return '12AM';
  if (hour === 12) return '12PM';
  if (hour < 12) return `${hour}AM`;
  return `${hour - 12}PM`;
}

/**
 * Format a large number with K/M suffixes
 */
function formatCompact(num) {
  if (num == null) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

/**
 * Format number with commas
 */
function formatNumber(num) {
  return num?.toLocaleString() ?? '0';
}

/**
 * Format minutes into readable duration
 */
function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return 'N/A';
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * Get first name from full name
 */
function firstName(name) {
  if (!name || name === 'N/A') return name;
  return name.split(' ')[0];
}

/**
 * Check if stat has valid data to display
 */
function hasData(stat, countKey = 'count') {
  return stat && stat.name && stat.name !== 'N/A' && stat[countKey] > 0;
}

// =============================================================================
// DESIGN TOKENS
// =============================================================================

/** Typography tokens — strict two-scale system */
const TYP_LABEL = 'text-[10px] font-semibold uppercase tracking-wider';
const TYP_STAT  = 'text-lg font-bold text-ink leading-tight';
const TYP_SUB   = 'text-xs text-muted';

/** Accent styles: label color only, no border stripe */
const ACCENT_STYLES = {
  rust:   '',
  forest: '',
  gold:   '',
};

/** Accent label color — full-strength on tiny labels for clear color signal */
const ACCENT_LABEL_CLASS = {
  rust:   'text-rust',
  forest: 'text-forest',
  gold:   'text-gold',
};

/** Size variants: padding + min-height */
const SIZE_CLASSES = {
  default: 'p-4 sm:p-5 min-h-[88px]',
  hero:    'p-6 sm:p-8 min-h-[140px] sm:min-h-[160px]',
  wide:    'p-4 sm:p-5 min-h-[108px] sm:min-h-[120px]',
  small:   'p-3 sm:p-4 min-h-[80px]',
};

// =============================================================================
// TILE COMPONENTS
// =============================================================================

/**
 * Bento tile — clean card with optional accent stripe
 * @param {'default'|'hero'|'wide'|'small'} size
 * @param {boolean|'forest'|'gold'} accent - true = rust
 * @param {boolean} hero - legacy shorthand for size="hero"
 */
export function BentoTile({
  children,
  className = '',
  colSpan = 1,
  rowSpan = 1,
  accent = false,
  hero = false,
  size: sizeProp,
  onClick = null,
}) {
  const spanClasses = {
    1: '',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-2 sm:col-span-4',
  };

  const rowSpanClasses = {
    1: '',
    2: 'row-span-2',
  };

  const accentClass = accent === true ? ACCENT_STYLES.rust
    : (accent === 'forest' || accent === 'gold') ? ACCENT_STYLES[accent]
    : '';

  const size = sizeProp || (hero ? 'hero' : 'default');
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.default;

  return (
    <div
      className={`
        bg-white rounded-xl border border-parchment/30
        transition-all duration-200 ease-out
        hover:shadow-md hover:border-parchment/50
        h-full flex flex-col justify-center
        ${sizeClass}
        ${accentClass}
        ${spanClasses[colSpan] || ''}
        ${rowSpanClasses[rowSpan] || ''}
        ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''}
        ${className}
      `}
      onClick={onClick}
    >
      {children}
    </div>
  );
}

/** Icon background and value text color per accent (StatTile) */
const STAT_ACCENT_ICON = { rust: 'bg-rust/10', forest: 'bg-forest/10', gold: 'bg-gold/10' };
const STAT_ACCENT_VALUE = { rust: 'text-rust', forest: 'text-forest', gold: 'text-gold' };

/**
 * Mini stat tile for single values
 */
export function StatTile({ icon, label, value, sublabel, accent = false }) {
  const accentKey = accent === true ? 'rust' : (accent === 'forest' || accent === 'gold') ? accent : null;
  const iconClass = accentKey ? STAT_ACCENT_ICON[accentKey] : 'bg-parchment/50';
  const valueClass = accentKey ? STAT_ACCENT_VALUE[accentKey] : 'text-ink';
  return (
    <BentoTile accent={accent}>
      <div className="flex flex-col items-center text-center">
        {icon && (
          <div className={`w-10 h-10 ${iconClass} rounded-xl flex items-center justify-center mb-2`}>
            {icon}
          </div>
        )}
        <p className={`${TYP_STAT} ${valueClass}`}>{value}</p>
        <p className={`${TYP_SUB} mt-0.5`}>{label}</p>
        {sublabel && (
          <p className={`${TYP_SUB} mt-0.5`}>{sublabel}</p>
        )}
      </div>
    </BentoTile>
  );
}

/**
 * Timeline stat tile with emoji icon — left-aligned label / stat / subtitle
 */
export function TimelineTile({ emoji, label, value, sublabel, accent = false, rowSpan = 1 }) {
  const accentKey = accent === true ? 'rust' : (accent === 'forest' || accent === 'gold') ? accent : null;
  const labelColor = accentKey ? ACCENT_LABEL_CLASS[accentKey] : 'text-muted';
  return (
    <BentoTile accent={accent} rowSpan={rowSpan}>
      <div className="flex items-start gap-3">
        <span className="text-xl flex-shrink-0 mt-0.5">{emoji}</span>
        <div className="min-w-0 flex-1">
          <p className={`${TYP_LABEL} mb-1 ${labelColor}`}>{label}</p>
          <p className={`${TYP_STAT} truncate`}>{value}</p>
          {sublabel && (
            <p className={`${TYP_SUB} mt-1`}>{sublabel}</p>
          )}
        </div>
      </div>
    </BentoTile>
  );
}

/**
 * Most active person tile — left-aligned
 */
export function MostActiveTile({ name, percentage, count, sublabel }) {
  return (
    <BentoTile accent>
      <p className={`${TYP_LABEL} text-rust mb-1`}>Most Active</p>
      <p className={`${TYP_STAT} truncate`}>{name}</p>
      <p className="text-xl font-bold text-rust mt-0.5">{percentage}%</p>
      <p className={`${TYP_SUB} mt-0.5`}>{formatCompact(count)} msgs</p>
      {sublabel && <p className={`${TYP_SUB} mt-0.5`}>{sublabel}</p>}
    </BentoTile>
  );
}

/**
 * Leaderboard row
 */
function LeaderRow({ rank, name, count, percentage, isTop = false }) {
  return (
    <div className={`
      flex items-center gap-2.5 py-1.5 px-2 rounded-md
      ${isTop ? 'bg-rust/8' : 'hover:bg-parchment/30'}
      transition-colors
    `}>
      <span className={`
        w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0
        ${isTop ? 'bg-rust text-white' : 'bg-parchment/70 text-sepia'}
      `}>
        {rank}
      </span>
      <span className={`flex-1 truncate text-xs ${isTop ? 'font-semibold text-ink' : 'text-sepia'}`}>
        {name}
      </span>
      <div className="text-right flex-shrink-0">
        <span className="text-xs font-semibold text-ink">{formatCompact(count)}</span>
        <span className="text-[10px] text-muted ml-1">{percentage}%</span>
      </div>
    </div>
  );
}

/**
 * Leaderboard tile — wider card with accent stripe
 */
export function LeaderboardTile({ topSenders, colSpan = 2, limit = 5 }) {
  if (!topSenders || topSenders.length === 0) return null;

  return (
    <BentoTile colSpan={colSpan} size="wide" accent>
      <div className="h-full flex flex-col justify-start">
        <p className={`${TYP_LABEL} text-rust mb-2.5`}>Top Contributors</p>
        <div className="space-y-0.5">
          {topSenders.slice(0, limit).map((sender, i) => (
            <LeaderRow
              key={sender.name}
              rank={i + 1}
              name={sender.name}
              count={sender.count}
              percentage={sender.percentage}
              isTop={i === 0}
            />
          ))}
        </div>
      </div>
    </BentoTile>
  );
}

/**
 * Personality tile — compact/small, center-aligned
 */
export function PersonalityTile({ emoji, label, name, sublabel, accent = false }) {
  const accentKey = accent === true ? 'rust' : (accent === 'forest' || accent === 'gold') ? accent : null;
  const labelColor = accentKey ? ACCENT_LABEL_CLASS[accentKey] : 'text-muted';
  return (
    <BentoTile accent={accent} size="small">
      <div className="text-center">
        <span className="text-lg">{emoji}</span>
        <p className={`${TYP_LABEL} mt-1 ${labelColor}`}>{label}</p>
        <p className={`${TYP_STAT} text-base truncate mt-0.5`}>{name}</p>
        {sublabel && (
          <p className={`${TYP_SUB} mt-0.5`}>{sublabel}</p>
        )}
      </div>
    </BentoTile>
  );
}

// =============================================================================
// STATISTICS HOOK
// =============================================================================

/**
 * Hook to fetch statistics data
 */
export function useStatistics(sessionId) {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchStats() {
      if (!sessionId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await getStatistics(sessionId);
        setStatistics(data);
      } catch (err) {
        console.error('Failed to fetch statistics:', err);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [sessionId]);

  return { statistics, loading, error };
}

// =============================================================================
// SECTION BUILDER
// =============================================================================

/**
 * Build tile arrays grouped by section.
 * Returns { activity, social, badges, rhythm }.
 */
function collectSections(statistics) {
  const {
    timeline,
    participation,
    volume,
    energy,
    personality,
    text_analysis,
    time_patterns,
    milestones,
  } = statistics;

  // === ROW 2: Activity (3 equal cards) ===
  const activity = [];
  activity.push(
    <MostActiveTile
      key="most-active"
      name={participation.most_active.name}
      percentage={participation.most_active.percentage}
      count={participation.most_active.count}
      sublabel="Main character energy"
    />
  );
  activity.push(
    <TimelineTile
      key="chaotic-day"
      emoji="🔥"
      label="Most Chaotic Day"
      value={timeline.most_active_day}
      sublabel={`${formatNumber(timeline.most_active_day_count)} messages · Certified madness`}
    />
  );
  if (volume?.avg_per_day > 0) {
    activity.push(
      <TimelineTile
        key="daily-avg"
        emoji="📊"
        label="Daily Average"
        value={`${Math.round(volume.avg_per_day)}`}
        sublabel="messages/day"
        accent="forest"
      />
    );
  }

  // === ROW 3: People + Silence (asymmetric: Leaderboard 3-col + Silence 1-col rowSpan 2) ===
  const social = [];
  if (participation.top_senders?.length > 0) {
    social.push(
      <LeaderboardTile
        key="leaderboard"
        topSenders={participation.top_senders}
        colSpan={3}
        limit={3}
      />
    );
  }
  social.push(
    <TimelineTile
      key="silence"
      emoji="📅"
      label="Longest Silence"
      value={`${timeline.longest_silence_days} days`}
      accent="forest"
      rowSpan={2}
    />
  );

  // === ROW 4: Personality badges (exactly 4) ===
  const badges = [];
  if (hasData(energy?.emoji_champion)) {
    badges.push(
      <PersonalityTile
        key="emoji-champion"
        emoji="😎"
        label="Emoji Champion"
        name={firstName(energy.emoji_champion.name)}
        sublabel={`${formatCompact(energy.emoji_champion.count)} emojis`}
        accent="gold"
      />
    );
  }
  if (hasData(energy?.question_master)) {
    badges.push(
      <PersonalityTile
        key="question-master"
        emoji="❓"
        label="Question Master"
        name={firstName(energy.question_master.name)}
        sublabel={`${formatCompact(energy.question_master.count)} questions`}
        accent="gold"
      />
    );
  }
  // Smart pick: Night Owl vs Early Bird — whichever has higher count
  const nightOwl = personality?.night_owl;
  const earlyBird = personality?.early_bird;
  const useNightOwl = (nightOwl?.count || 0) >= (earlyBird?.count || 0);
  const timePersonality = useNightOwl ? nightOwl : earlyBird;
  if (timePersonality && hasData(timePersonality)) {
    badges.push(
      <PersonalityTile
        key={useNightOwl ? 'night-owl' : 'early-bird'}
        emoji={useNightOwl ? '🦉' : '☀️'}
        label={useNightOwl ? 'Night Owl' : 'Early Bird'}
        name={firstName(timePersonality.name)}
        sublabel={useNightOwl
          ? `${formatCompact(timePersonality.count)} late msgs`
          : `${formatCompact(timePersonality.count)} AM msgs`}
      />
    );
  }

  // === ROW 5: Time + Milestone (3 equal cards) ===
  const rhythm = [];
  if (time_patterns?.favorite_day?.day_name && time_patterns.favorite_day.day_name !== 'N/A') {
    rhythm.push(
      <TimelineTile
        key="favorite-day"
        emoji="📅"
        label="Favorite Day"
        value={time_patterns.favorite_day.day_name}
        sublabel={`${formatCompact(time_patterns.favorite_day.count)} messages`}
        accent="forest"
      />
    );
  }
  if (time_patterns?.busiest_month?.month_name && time_patterns.busiest_month.month_name !== 'N/A') {
    rhythm.push(
      <TimelineTile
        key="busiest-month"
        emoji="📈"
        label="Busiest Month"
        value={time_patterns.busiest_month.month_name}
        sublabel={`${formatCompact(time_patterns.busiest_month.count)} messages`}
        accent="forest"
      />
    );
  }
  if (time_patterns?.streak_record?.days > 1) {
    rhythm.push(
      <TimelineTile
        key="streak"
        emoji="🔥"
        label="Longest Streak"
        value={`${time_patterns.streak_record.days} days`}
        sublabel={time_patterns.streak_record.start_date}
        accent="forest"
      />
    );
  }

  return { activity, social, badges, rhythm };
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Statistics Bento Grid — renders section-grouped grids with
 * larger spacing between sections than within.
 */
export default function StatisticsDisplay({ sessionId }) {
  const { statistics, loading, error } = useStatistics(sessionId);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-rust/20 border-t-rust rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !statistics) {
    return null;
  }

  const sections = collectSections(statistics);

  return (
    <div className="space-y-5">
      {/* Activity row: 3-col */}
      {sections.activity.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {sections.activity}
        </div>
      )}

      {/* Social + Badges: 4-col (Leaderboard 3-col + Silence rowSpan 2, then badges) */}
      {(sections.social.length > 0 || sections.badges.length > 0) && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {sections.social}
          {sections.badges}
        </div>
      )}

      {/* Rhythm row: up to 3-col */}
      {sections.rhythm.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {sections.rhythm}
        </div>
      )}
    </div>
  );
}
